import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { CurrentOpenMenu } from '@/pages/Search/SearchPage/types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchSearchAggregationsOnDemand } from '@/redux/modules/search/search.actions';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { getRenderedPriorityFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { Distance } from '@/types/Distance';
import FacetTypes from '@/types/search/enums/facetTypes';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { isArchivedSearch, parseQueryParams } from '@/utils/queryParams';

import { AllFilters } from './components/AllFilters/AllFilters';
import { AuctionHouseFilter } from './components/AllFilters/components/AuctionHouseFilter/AuctionHouseFilter';
import { CategoryFilter } from './components/CategoryFilter/CategoryFilter';
import { CreatorPill } from './components/CreatorPill/CreatorPill';
import { ExcludeAuctionHousesFilter } from './components/ExcludeAuctionHousesFilter/ExcludeAuctionHousesFilter';
import { KeywordFilter } from './components/KeywordFilter/KeywordFilter';
import { LocationFilter } from './components/LocationFilter/LocationFilter';
import { MaterialsAndTechniquesPill } from './components/MaterialsAndTechniquesPill/MaterialsAndTechniquesPill';
import { PreferencesFilters } from './components/PreferencesFilters/PreferencesFilters';
import { ShippingFilter } from './components/ShippingFilter/ShippingFilter';
import { SortFilter } from './components/SortFilter/SortFilter';
import { StylePeriodPill } from './components/StylePeriodPill/StylePeriodPill';
import { SUBJECT_FACET_ID, SubjectPill } from './components/SubjectPill/SubjectPill';
import { useDragToScroll } from './hooks/useDragToScroll';

type Props = {
    distance: Distance;
    searchTerm: string;
};

// Intersected with the backend's `priorityFacets` array so unknown ids fall
// out before the composition decision — otherwise an all-unknown list would
// render an empty pill row between AllFilters and the global pills.
const KNOWN_PRIORITY_FACET_IDS: ReadonlySet<string> = new Set([
    FacetTypes.STYLE_PERIOD_ID,
    SUBJECT_FACET_ID,
    FacetTypes.CREATOR_ID,
    FacetTypes.MATERIALS_AND_TECHNIQUES_ID,
]);

// On-demand aggregations fetch trigger list. Any pill that reads facet options
// from state.search.facets needs membership here so its first open hydrates
// the facets — otherwise skipAggs initial loads render the pill's empty-options
// branch and the user has no way to trigger the fetch.
const MENUS_REQUIRING_AGGREGATES_ON_DEMAND: ReadonlySet<CurrentOpenMenu> = new Set([
    CurrentOpenMenu.AuctionHouse,
    CurrentOpenMenu.Category,
    CurrentOpenMenu.Creator,
    CurrentOpenMenu.Location,
    CurrentOpenMenu.MaterialsAndTechniques,
    CurrentOpenMenu.Shipping,
    CurrentOpenMenu.StylePeriod,
    CurrentOpenMenu.Subject,
]);

/** ZRP sort options take precedence when both isZeroResults and archivedSearch are true. */
const getPermittedSortOptions = (isZeroResults: boolean, archivedSearch: boolean): SearchSortAndDirection[] => {
    if (isZeroResults) {
        return [
            SearchSortAndDirection.MostRelevant,
            SearchSortAndDirection.TimeNewest,
            SearchSortAndDirection.HammerPriceHighest,
        ];
    }
    if (!archivedSearch) {
        return [
            SearchSortAndDirection.MostRelevant,
            SearchSortAndDirection.TimeMostRecent,
            SearchSortAndDirection.NewlyListed,
            SearchSortAndDirection.PriceLowest,
            SearchSortAndDirection.PriceHighest,
        ];
    }
    return [];
};

export const SearchFilters = ({ distance, searchTerm }: Props) => {
    const { pathname, search, state } = useLocation();
    const queryParams = parseQueryParams(search);
    const dispatch = useAppDispatch();

    const [currentOpenMenu, setCurrentOpenMenu] = useState<CurrentOpenMenu | undefined>(
        typeof state === 'object' ? state?.currentOpenMenu : undefined
    );
    const [showAllFiltersDrawer, setShowAllFiltersDrawer] = useState(currentOpenMenu === CurrentOpenMenu.AllFilters);
    const [showPreferencesDrawer, setShowPreferencesDrawer] = useState(false);

    const isMobile = useWindowIsMobile();

    const containerRef = useRef<HTMLDivElement>(null);

    const { isDragging } = useDragToScroll(containerRef, { disableScroll: showAllFiltersDrawer });

    const archivedSearch = isArchivedSearch(queryParams, pathname);

    const isLoggedIn = useAppSelector(isUserLoggedIn);

    const { isZeroResults } = useAppSelector(getSearchUiState);

    const priorityFacets = useAppSelector(getRenderedPriorityFacets);

    const handleMenuOpen = useCallback(
        (menu: CurrentOpenMenu) => {
            if (MENUS_REQUIRING_AGGREGATES_ON_DEMAND.has(menu)) {
                dispatch(fetchSearchAggregationsOnDemand());
            }
            setCurrentOpenMenu(menu);
        },
        [dispatch]
    );

    // Drop unknown priority ids before the composition gate so an all-unknown
    // list (e.g. backend ships a new id before the FE registers a wrapper for
    // it) falls back to the default pill row instead of rendering an empty one.
    const renderablePriorityFacets = useMemo(
        () => priorityFacets.filter((id) => KNOWN_PRIORITY_FACET_IDS.has(id)),
        [priorityFacets]
    );

    // Local render helper; consumed only by the inline `.map()` below, so
    // there's no memoized child depending on its identity. Plain function
    // keeps the render path simple and avoids a useCallback dep that
    // would invalidate on every menu open.
    const renderPriorityPill = (facetId: string): ReactNode => {
        const closeMenu = () => {
            setCurrentOpenMenu(undefined);
        };
        switch (facetId) {
            case FacetTypes.STYLE_PERIOD_ID:
                return (
                    <StylePeriodPill
                        currentOpenMenu={currentOpenMenu}
                        key={facetId}
                        onMenuClose={closeMenu}
                        onMenuOpen={handleMenuOpen}
                    />
                );
            case SUBJECT_FACET_ID:
                return (
                    <SubjectPill
                        currentOpenMenu={currentOpenMenu}
                        key={facetId}
                        onMenuClose={closeMenu}
                        onMenuOpen={handleMenuOpen}
                    />
                );
            case FacetTypes.CREATOR_ID:
                return (
                    <CreatorPill
                        currentOpenMenu={currentOpenMenu}
                        key={facetId}
                        onMenuClose={closeMenu}
                        onMenuOpen={handleMenuOpen}
                    />
                );
            case FacetTypes.MATERIALS_AND_TECHNIQUES_ID:
                return (
                    <MaterialsAndTechniquesPill
                        currentOpenMenu={currentOpenMenu}
                        key={facetId}
                        onMenuClose={closeMenu}
                        onMenuOpen={handleMenuOpen}
                    />
                );
            default:
                return null;
        }
    };

    const showPriorityPills = renderablePriorityFacets.length > 0;

    const permittedSortOptions = useMemo(
        () => getPermittedSortOptions(isZeroResults, archivedSearch),
        [archivedSearch, isZeroResults]
    );

    const defaultSort = useMemo(() => (isZeroResults ? SearchSortAndDirection.TimeNewest : undefined), [isZeroResults]);

    if (isMobile) {
        return (
            <div className="mt-sm mb-md w-full items-center gap-sm xsMax:flex">
                <AllFilters
                    currentOpenMenu={currentOpenMenu}
                    defaultSort={defaultSort}
                    distance={distance}
                    isArchivedSearch={archivedSearch}
                    onMenuClose={() => {
                        setCurrentOpenMenu(undefined);
                    }}
                    onMenuOpen={setCurrentOpenMenu}
                    permittedSortOptions={permittedSortOptions}
                    setShowAllFiltersDrawer={setShowAllFiltersDrawer}
                    showAllFiltersDrawer={showAllFiltersDrawer}
                />
                {isLoggedIn && !archivedSearch && !isZeroResults && (
                    <div className="xsMax:w-full">
                        <PreferencesFilters
                            setShowPreferencesDrawer={setShowPreferencesDrawer}
                            showPreferencesDrawer={showPreferencesDrawer}
                        />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            className={cn(
                'relative m-auto flex max-w-[1644px] items-start justify-between gap-md bg-background-primary py-md',
                {
                    'border-t-0': !searchTerm,
                }
            )}
            data-testid="search-filters-container"
        >
            <KeywordFilter />
            <div
                className={cn(
                    'flex w-full items-center gap-xs overflow-x-auto',
                    '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
                    'select-none',
                    {
                        'cursor-grabbing': isDragging,
                        'overflow-x-hidden': showAllFiltersDrawer,
                    }
                )}
                data-testid="scroll-container"
                ref={containerRef}
            >
                <AllFilters
                    currentOpenMenu={currentOpenMenu}
                    defaultSort={defaultSort}
                    distance={distance}
                    isArchivedSearch={archivedSearch}
                    onMenuClose={() => {
                        setCurrentOpenMenu(undefined);
                    }}
                    onMenuOpen={setCurrentOpenMenu}
                    permittedSortOptions={permittedSortOptions}
                    setShowAllFiltersDrawer={setShowAllFiltersDrawer}
                    showAllFiltersDrawer={showAllFiltersDrawer}
                />
                {showPriorityPills ? (
                    // Art surfaces: drop the default Category / Location / Shipping /
                    // Auction House row in favor of the backend's priority pills.
                    // AllFilters and ExcludeAuctionHousesFilter still wrap the
                    // row so non-priority facets remain reachable.
                    renderablePriorityFacets.map(renderPriorityPill)
                ) : (
                    <>
                        <CategoryFilter
                            currentOpenMenu={currentOpenMenu}
                            onMenuClose={() => {
                                setCurrentOpenMenu(undefined);
                            }}
                            onMenuOpen={handleMenuOpen}
                        />

                        <LocationFilter
                            currentOpenMenu={currentOpenMenu}
                            distance={distance}
                            isArchivedSearch={archivedSearch}
                            onMenuClose={() => {
                                setCurrentOpenMenu(undefined);
                            }}
                            onMenuOpen={handleMenuOpen}
                        />
                        <ShippingFilter
                            currentOpenMenu={currentOpenMenu}
                            isArchivedSearch={archivedSearch}
                            onMenuClose={() => {
                                setCurrentOpenMenu(undefined);
                            }}
                            onMenuOpen={handleMenuOpen}
                        />
                        <AuctionHouseFilter
                            currentOpenMenu={currentOpenMenu}
                            onMenuClose={() => {
                                setCurrentOpenMenu(undefined);
                            }}
                            onMenuOpen={handleMenuOpen}
                        />
                    </>
                )}
                {!archivedSearch && !isZeroResults && <ExcludeAuctionHousesFilter />}
            </div>
            <SortFilter
                defaultSort={defaultSort}
                permittedSortOptions={permittedSortOptions}
            />
        </div>
    );
};
